// src/components/ui/NumPad.tsx — context-sensitive PIN keypad.
//
// Verbatim port of docs/prototype/auth.jsx lines 42-88. When the next slot
// in the PIN is a letter (positions 0 or 2) render a row of four letter
// buttons (K F W T). When the next slot is a digit, render the 3x4 numpad
// with delete and submit. The OK button is disabled until the PIN buffer
// hits length 6.

import type { CSSProperties } from "react";
import { C, F } from "../../theme";
import {
  PIN_LENGTH,
  PIN_LETTERS,
  positionNeedsLetter,
  type PinLetter,
} from "./PinDots";

export interface NumPadProps {
  /** Current PIN buffer length. Used to decide letter-vs-digit layout and OK enable. */
  length: number;
  onPress: (char: string) => void;
  onDelete: () => void;
  onSubmit: () => void;
  /** When true (e.g. submitting), disable all input. */
  disabled?: boolean;
}

const wrapStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 10,
};

const captionStyle: CSSProperties = {
  fontFamily: F.serif,
  fontStyle: "italic",
  fontSize: 12,
  color: C.brassDk,
  fontWeight: 600,
  letterSpacing: 0.3,
};

function keyStyle(highlight: boolean = false): CSSProperties {
  return {
    width: 74,
    height: 74,
    borderRadius: 18,
    border: `1px solid ${C.hairline}`,
    background: highlight ? C.brassDk : C.card,
    color: highlight ? C.brassGlow : C.ink,
    fontSize: 26,
    fontWeight: 700,
    fontFamily: F.serif,
    cursor: "pointer",
    boxShadow: C.shadowS,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    WebkitTapHighlightColor: "transparent",
    touchAction: "manipulation",
  };
}

export const NumPad = ({ length, onPress, onDelete, onSubmit, disabled }: NumPadProps) => {
  const needsLetter = positionNeedsLetter(length);

  if (needsLetter) {
    return (
      <div style={wrapStyle}>
        <div style={captionStyle}>Pick a letter</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 60px)", gap: 10, justifyContent: "center" }}>
          {PIN_LETTERS.map((L: PinLetter) => (
            <button
              key={L}
              type="button"
              onClick={() => onPress(L)}
              disabled={disabled}
              style={{ ...keyStyle(), width: 60, height: 74, fontSize: 22, opacity: disabled ? 0.5 : 1 }}
            >
              {L}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={onDelete}
          disabled={disabled || length === 0}
          style={{
            ...keyStyle(),
            width: "auto",
            padding: "0 22px",
            height: 48,
            fontSize: 14,
            fontFamily: F.body,
            fontWeight: 800,
            background: C.paperWarm,
            color: C.inkSoft,
            opacity: disabled || length === 0 ? 0.5 : 1,
          }}
        >
          ← Delete
        </button>
      </div>
    );
  }

  const ready = length === PIN_LENGTH;
  return (
    <div style={wrapStyle}>
      <div style={captionStyle}>Pick a number</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 74px)", gap: 10, justifyContent: "center" }}>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => onPress(String(d))}
            disabled={disabled}
            style={{ ...keyStyle(), opacity: disabled ? 0.5 : 1 }}
          >
            {d}
          </button>
        ))}
        <button
          type="button"
          onClick={onDelete}
          disabled={disabled || length === 0}
          style={{
            ...keyStyle(),
            background: C.paperWarm,
            color: C.inkSoft,
            fontSize: 14,
            fontWeight: 800,
            fontFamily: F.body,
            opacity: disabled || length === 0 ? 0.5 : 1,
          }}
        >
          Del
        </button>
        <button
          type="button"
          onClick={() => onPress("0")}
          disabled={disabled}
          style={{ ...keyStyle(), opacity: disabled ? 0.5 : 1 }}
        >
          0
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!ready || disabled}
          style={{
            ...keyStyle(ready),
            opacity: ready && !disabled ? 1 : 0.5,
            cursor: ready && !disabled ? "pointer" : "default",
            fontSize: 16,
            fontFamily: F.body,
            fontWeight: 800,
          }}
        >
          OK
        </button>
      </div>
    </div>
  );
};
